// MarketplaceTemplateForm
import React, { useEffect, useState } from 'react'
import { useStyles } from './MarketplaceTemplateForm.styles'
import {
  ButtonV2,
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  ModalConfirmV2,
  ModalSizeVariants,
  SelectV4,
  TextInputV2,
  TextInputV4,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faPen } from '@fortawesome/pro-regular-svg-icons'
import HubspotImage from '../../../common/images/HubSpot_Logo.svg'
import SalesforceImage from '../../../common/images/salesforce.svg'
import { MarketplaceTemplateFormFooter } from './MarketplaceTemplateFormFooter/MarketplaceTemplateFormFooter'
import { MarketplaceTemplateTableContainer } from './MarketplaceTemplateTableContainer/MarketplaceTemplateTableContainer'
import { MarketplaceTemplateTableContainerSalesforce } from './MarketplaceTemplateTableContainer/MarketplaceTabConfigurationWrapper/MarketplaceTemplateTabContainerWrapperSalesforce'
import { AzureMarketplaceTemplateTableContainer } from './AzureMarketplaceTemplateTableContainer/AzureMarketplaceTemplateTableContainer'
import { useDispatch, useSelector } from '../../../store'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useUserType, useAccountId } from '@labrav/common'
import { isEmpty } from 'lodash'
import {
  cloneMarketplaceReferralTemplate,
  deleteMarketplaceReferralTemplate,
  getAllMarketplaceHubspotFields,
  getAllSalesforceFields,
  getMarketplaceReferralTemplate,
  getMarketplaceReferralTemplateSalesforce,
  updateMarketplaceReferralTemplateName,
} from '../../../modules/marketplaceMappingManager/action'
import { LoadingStateComponent } from '@labrav/common'
import { CircularProgress } from '@mui/material'
import { useTracker } from '@labrav/common'
import { snakeize } from 'casing'
import { UserProfile } from '@labrav/common'
import { ShortenedNameV2 } from '@labrav/common'
import { CRMTypes } from '../../../utils/constants'
import { CloudType } from '../../../types/external'
import { LoadingTypes, isLoading as Loading } from '@labrav/common'

const ColumnActionWithMenuOptions = [
  {
    key: 'clone-template',
    label: 'Clone',
  },
  {
    key: 'delete-template',
    label: 'Delete',
  },
  {
    key: 'view-activity-log',
    label: 'View activity log',
    disable: true,
    disabledTooltip: 'Coming soon',
  },
]

export const MarketplaceTemplateForm: React.FC = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isViewOnly = queryParams.get('viewOnly') === 'true'
  const { classes } = useStyles({ isViewOnly })
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const partnerType = useUserType()
  const tracker = useTracker()
  const { templateId = '' } = useParams()
  const crmId = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmId || ''
  )
  const crmType = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmType || ''
  )

  const { cloudType, accountId: routeAccountId } = useParams<{
    cloudType?: string
    accountId?: string
  }>()
  const cloudForAccountLookup = (cloudType || 'aws').toUpperCase() as CloudType
  const accountId =
    routeAccountId ||
    useAccountId({
      product: 'flyout',
      cloud: cloudForAccountLookup,
    }) ||
    ''
  const user = useSelector<UserProfile | undefined>(
    state => state.userProfile.userProfile
  )
  const username = `${user?.givenName || ''} ${user?.familyName || ''}`.trim()

  const currentTemplateData = useSelector(
    state => state.marketplaceMappingManager.templateData.templateData
  )
  const partnerUserId = useSelector<string | undefined>(
    state => state.userProfile.userProfile.id
  ) as string

  const isEditTemplateNameLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.EDIT_REFERRAL_TEMPLATE_NAME)
  )
  const isReferralTemplatePageLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
  )
  const isHubspotFieldDataLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_HUBSPOT_FIELDS)
  )
  const isSalesforceFieldDataLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_CRM_FIELDS)
  )
  const isDiscardLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.DISCARD_REFERRAL_TEMPLATE)
  )

  const [openEditTemplateModal, setOpenEditTemplateModal] =
    useState<boolean>(false)
  const [editTemplateName, setEditTemplateName] = useState('')
  const [editNameError, setEditNameError] = useState('')
  const [acceptDisabled, setAcceptDisabled] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [fieldFilter, setFieldFilter] = useState<'all' | 'mapped' | 'unmapped'>(
    'all'
  )
  const isSalesforce = crmType === CRMTypes.salesforce
  const isHubspot = crmType === CRMTypes.hubspot
  const templateTypeLabel =
    currentTemplateData?.templateType === 'resale_auth'
      ? 'Reseller authorization'
      : 'Offer'
  const usesAzureMappingService =
    isHubspot && cloudType?.toUpperCase() === CloudType.AZURE && !!accountId
  const isAzureHubspot =
    isHubspot &&
    (usesAzureMappingService ||
      cloudType?.toUpperCase() === CloudType.AZURE ||
      (!cloudType && !!currentTemplateData?.useTemplateFromMappingService))

  const trackEvent = ({
    action,
    title,
    section = 'Marketplace Templates',
    component,
    customData = {},
  }: {
    action: string
    title: string
    section?: string
    component?: string
    customData?: Record<string, any>
  }) => {
    const snakeCaseSectionName = snakeize(section)
    const snakeCaseComponentName = snakeize(
      component || 'MarketplaceTemplateForm'
    )
    tracker?.track({
      section: `marketplace_mapping_manager-${snakeCaseSectionName}`,
      component: snakeCaseComponentName,
      action,
      title,
      partnerId: `${crmId}`,
      customerName: `${username}`,
      ...customData,
    })
  }

  const handleCloseEditTemplateModal = () => {
    setOpenEditTemplateModal(false)
    setEditTemplateName('')
    setEditNameError('')
  }
  const handleEditTemplateIconClick = () => {
    trackEvent({
      action: 'edit-template-icon-click',
      title: 'Edit Template Icon Click',
    })
    setEditTemplateName(currentTemplateData?.name)
    setOpenEditTemplateModal(true)
  }

  const handleEditNameBlur = () => {
    if (!editTemplateName.trim()) {
      setEditNameError('Template name is required.')
      setAcceptDisabled(true)
    }
  }

  const handleEditNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEditTemplateName(value)
    if (!value.trim()) {
      setEditNameError('Template name is required.')
      setAcceptDisabled(true)
    } else if (value.length > 255) {
      setEditNameError('Template name must be less than 255 characters.')
      setAcceptDisabled(true)
    } else {
      setEditNameError('')
      setAcceptDisabled(false)
    }
  }

  const handleSaveEditTemplate = async () => {
    const isNameEdited = (await dispatch(
      updateMarketplaceReferralTemplateName(
        crmId,
        currentTemplateData.id,
        {
          name: editTemplateName,
          ...(crmType === CRMTypes.salesforce
            ? { updatedBy: partnerUserId }
            : {}),
        },
        accountId,
        crmType === CRMTypes.salesforce
          ? !!accountId
          : currentTemplateData?.useTemplateFromMappingService,
        crmType === CRMTypes.salesforce
      )
    )) as unknown as boolean
    if (isNameEdited) {
      setOpenEditTemplateModal(false)
      trackEvent({
        action: 'save-template-name',
        title: 'Save Template Name',
        component: 'EditTemplateModal',
        customData: { newName: editTemplateName },
      })
    } else {
      setEditNameError('Template already exists. Please enter a unique name.')
      setOpenEditTemplateModal(true)
    }
  }

  const handleSelectOption = async (option: ColumnMenuOption) => {
    if (option.key === 'clone-template') {
      const { templateId } = (await dispatch(
        cloneMarketplaceReferralTemplate(
          {
            name: `Clone_${currentTemplateData.name}`,
            ...(usesAzureMappingService
              ? { templateId: currentTemplateData.id }
              : { templateType: 'flyout' }),
            createdBy: partnerUserId,
          },
          crmId,
          currentTemplateData.id || '',
          accountId,
          crmType as CRMTypes,
          usesAzureMappingService
        )
      )) as unknown as { templateId: string; errorMessage: string }
      if (!isEmpty(templateId)) {
        trackEvent({
          action: 'clone-template',
          title: 'Template Cloned',
          component: 'ColumnActionWithMenu',
          customData: { templateId },
        })
        navigate(
          usesAzureMappingService
            ? `/settings/azure/${accountId}/mappings/marketplace/templates/${templateId}`
            : `/settings/mappings/templates/${templateId}`
        )
      }
    } else if (option.key === 'delete-template') {
      await dispatch(
        deleteMarketplaceReferralTemplate(
          crmId,
          currentTemplateData.id || '',
          currentTemplateData.name || '',
          accountId,
          crmType as CRMTypes,
          usesAzureMappingService
        )
      )
      trackEvent({
        action: 'delete-template',
        title: 'Delete Template Clicked',
        component: 'ColumnActionMenu',
      })
      navigate(
        usesAzureMappingService
          ? `/settings/azure/${accountId}/mappings/marketplace`
          : `/settings/mappings/marketplace`
      )
    }
  }

  useEffect(() => {
    if (!isEmpty(crmId)) {
      if ((isSalesforce || usesAzureMappingService) && accountId) {
        if (isSalesforce) dispatch(getAllSalesforceFields(crmId, accountId))
        dispatch(
          getMarketplaceReferralTemplateSalesforce(
            crmId,
            accountId,
            templateId,
            undefined,
            usesAzureMappingService ? CRMTypes.hubspot : CRMTypes.salesforce
          )
        )
        if (usesAzureMappingService)
          dispatch(getAllMarketplaceHubspotFields(crmId))
      } else if (isHubspot) {
        dispatch(getAllMarketplaceHubspotFields(crmId))
        dispatch(getMarketplaceReferralTemplate(crmId, templateId))
      }
    }
  }, [
    accountId,
    crmId,
    isHubspot,
    isSalesforce,
    templateId,
    usesAzureMappingService,
  ])

  if (
    isHubspotFieldDataLoading ||
    isSalesforceFieldDataLoading ||
    isReferralTemplatePageLoading
  ) {
    return (
      <LoadingStateComponent
        heading="Loading your marketplace template form page"
        description="It may take a few moments, please wait..."
      />
    )
  }

  return (
    <div
      className={classes.root}
      data-testid="marketplace-template-form-container"
    >
      <div
        className={classes.topContainer}
        data-testid="marketplace-template-top-container"
      >
        <div className={classes.templateHeading}>
          {(isSalesforce || isAzureHubspot) && (
            <div className={classes.templateType}>{templateTypeLabel}</div>
          )}
          <div className={classes.templateNameContainer}>
            <div className={classes.name}>
              <ShortenedNameV2
                name={currentTemplateData?.name}
                length={20}
                showClipboardTooltip={true}
              />
            </div>
            <ButtonV2
              styleType="link"
              data-testid={'edit-template-name-btn'}
              smallVariant
              onClick={handleEditTemplateIconClick}
              disabled={isViewOnly}
            >
              <FontAwesomeIcon icon={faPen} className={classes.icon} />
            </ButtonV2>
          </div>
        </div>
        <img
          src={isHubspot ? HubspotImage : SalesforceImage}
          alt={isHubspot ? 'Hubspot Icon' : 'Salesforce Icon'}
          className={classes.hubspotIcon}
        />
      </div>
      <div
        className={classes.middleContainer}
        data-testid="marketplace-template-middle-container"
      >
        {isSalesforce && (
          <div className={classes.fieldSearch}>
            <div className={classes.fieldFilter}>
              <SelectV4
                minWidth="80px"
                field={{
                  title: '',
                  humanReadableKey: 'Filter fields',
                  placeHolder: 'All',
                  id: 'marketplace-field-filter',
                  isRequired: false,
                  isReadOnly: false,
                  isCustom: false,
                  dataType: 'enum',
                  value: fieldFilter,
                  options: [
                    { key: 'all', label: 'All', value: 'all' },
                    { key: 'mapped', label: 'Mapped', value: 'mapped' },
                    { key: 'unmapped', label: 'Unmapped', value: 'unmapped' },
                  ],
                }}
                onChange={event =>
                  setFieldFilter(event.target.value as typeof fieldFilter)
                }
              />
            </div>
            <div className={classes.fieldSearchInput}>
              <TextInputV4
                keyName="marketplace-field-search"
                title=""
                placeHolder="Search fields"
                type="text"
                value={searchTerm}
                updatedValue={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                isError={false}
                errorText=""
                isReadOnly={false}
                isRequired={false}
                showSpinner={false}
              />
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className={classes.fieldSearchIcon}
              />
            </div>
          </div>
        )}
        {!isAzureHubspot && (
          <div
            className={classes.columnActionContainer}
            data-testid="column-action-menu"
          >
            <ColumnActionWithMenuV2
              handleSelectOption={handleSelectOption}
              menuOptions={ColumnActionWithMenuOptions}
              disable={isViewOnly}
            />
          </div>
        )}
      </div>
      <div
        className={classes.bottomContainer}
        data-testid="marketplace-template-bottom-container"
      >
        {isDiscardLoading ? (
          <CircularProgress className={classes.loadingSpinner} />
        ) : (
          <>
            {isSalesforce ? (
              <MarketplaceTemplateTableContainerSalesforce
                searchTerm={searchTerm}
                fieldFilter={fieldFilter}
              />
            ) : isAzureHubspot ? (
              <AzureMarketplaceTemplateTableContainer
                handleSelectOption={handleSelectOption}
                menuOptions={ColumnActionWithMenuOptions}
                isViewOnly={isViewOnly}
              />
            ) : (
              <MarketplaceTemplateTableContainer />
            )}
          </>
        )}
      </div>
      <MarketplaceTemplateFormFooter />
      <ModalConfirmV2
        title="Edit template name"
        icon={
          <FontAwesomeIcon
            icon={faPen}
            data-testid="reset-mapping-icon-modal"
          />
        }
        onlyCloseModal
        onCloseModal={handleCloseEditTemplateModal}
        onDecline={handleCloseEditTemplateModal}
        content={
          <div>
            <TextInputV2
              keyName="edit-template-name"
              title="Enter a unique template name"
              placeHolder=""
              type="text"
              value={editTemplateName}
              onChange={handleEditNameChange}
              onBlur={handleEditNameBlur}
              isError={!!editNameError}
              helperText={!!editNameError}
              showSpinner={false}
              errorText={editNameError}
              isReadOnly={false}
              isRequired={true}
            />
          </div>
        }
        open={openEditTemplateModal}
        titleCancelButton={'Cancel'}
        titleAcceptButton={'Save'}
        onAccept={handleSaveEditTemplate}
        variant={ModalSizeVariants.medium}
        hideCancelButton={false}
        acceptDisabled={acceptDisabled}
        acceptLoading={isEditTemplateNameLoading}
      />
    </div>
  )
}
